import {
  BEAUTY_EFFECT_TYPE_KEYS,
  BeautyEffectTypeKey,
  BEAUTY_EFFECT_TYPE_BY_KEY,
  isBeautyEffectTypeKey,
} from '@hyperconnect/effects/dist/BeautyEffectType';
import {
  EffectRenderer,
  EffectTriggerEvent,
  EffectType,
} from '@hyperconnect/effects/dist/EffectRenderer';
import * as Sentry from '@sentry/nextjs';

import store from 'src/stores';
import {
  beautyEffectStrengthsAtom,
  getBeautyEffectStrength,
  handleDecoUnsupportAtom,
} from 'src/stores/deco/atom';
import { DecoEffect, EffectRendererName } from 'src/types/Deco';
import { ControlledPromise } from 'src/utils/controlled-promise';
import getDeviceInfo from 'src/utils/device/info';
import MediaStreamPipe from 'src/utils/media/pipes/base';
import { measure, setEffectWorkerRuntime } from 'src/utils/perf';

class EffectRendererPipe extends MediaStreamPipe {
  // SSR 불가능해서 런타임에 생성
  private static effectRenderer: EffectRenderer | null = null;

  private static initPromise: ControlledPromise<boolean> | null = null;

  /**
   * 이펙트 적용/해제 관련 로직이 동시 호출되면 이펙트 라이브러리 내에서 충돌 발생
   * 따라서 해당 task queue로 관련된 로직이 순차적으로 실행하여 원하는 렌더링 결과 보장
   */
  private static effectQueue: Promise<any> = Promise.resolve();

  public static getIsInitialized() {
    return EffectRendererPipe.effectRenderer !== null;
  }

  public static async initialize() {
    if (EffectRendererPipe.initPromise) {
      return EffectRendererPipe.initPromise.promise;
    }

    EffectRendererPipe.initPromise = new ControlledPromise<boolean>();

    try {
      const effectRenderer = new EffectRenderer({
        visionWorker: {
          enabled: true,
        },
      });

      const device = getDeviceInfo();

      const isInitSuccess = await effectRenderer.initialize({
        wasmFileset: '/mediapipe/tasksvision',
        face: {
          backend: 'CPU',
          modelPath: '/mediapipe/face_landmarker.task',
        },
        segmentation: {
          backend: 'GPU',
          modelPath: '/mediapipe/selfie_segmenter.tflite',
        },
        ...(!device.isMobile && {
          gesture: {
            backend: 'GPU',
            modelPath: '/mediapipe/gesture_recognizer.task',
          },
        }),
      });

      if (isInitSuccess) {
        EffectRendererPipe.effectRenderer = effectRenderer;
        setEffectWorkerRuntime(effectRenderer.isVisionWorkerRuntime());
      }
      EffectRendererPipe.initPromise.resolve(isInitSuccess);
    } catch (err) {
      EffectRendererPipe.initPromise.resolve(false);
    }
    return EffectRendererPipe.initPromise.promise;
  }

  public static removeAllEffects() {
    EffectRendererPipe.effectRenderer?.removeAll();
  }

  private static getBeautyEffectType(effectId: string) {
    const key = effectId.replace(/^DECO_WEB_BEAUTY_/, '') as BeautyEffectTypeKey;
    if (isBeautyEffectTypeKey(key)) {
      return BEAUTY_EFFECT_TYPE_BY_KEY[key];
    }
    return undefined;
  }

  private static enqueueTask<T>(task: () => Promise<T>): Promise<T> {
    const taskPromise = EffectRendererPipe.effectQueue.then(task);
    EffectRendererPipe.effectQueue = taskPromise.catch(() => {});
    return taskPromise;
  }

  public static getEffectType = (effect: DecoEffect) => {
    if (!effect.attributes) return null;
    if (BEAUTY_EFFECT_TYPE_KEYS.find((a) => a === effect.attributes?.effectRenderer[0])) {
      return EffectType.Beauty;
    }
    switch (effect.attributes.effectRenderer[0]) {
      case EffectRendererName.LUT_FILTER: {
        return EffectType.LUTFilter;
      }
      case EffectRendererName.LIQUIFY:
      case EffectRendererName.STRETCH: {
        return EffectType.FaceDistortion;
      }
      case EffectRendererName.SKIN_SMOOTH: {
        return EffectType.Beauty;
      }
      case EffectRendererName.TWO_D_STICKER:
      case EffectRendererName.THREE_D_STICKER:
      case EffectRendererName.MASK: {
        return EffectType.LegacyHeadgear;
      }
      case EffectRendererName.BACKGROUND:
      case EffectRendererName.BACKGROUND_BLUR: {
        return EffectType.BackgroundEffect;
      }
      case EffectRendererName.GESTURE: {
        return EffectType.Gesture;
      }
      default:
        return null;
    }
  };

  public static async removeEffect(effect: DecoEffect) {
    const effectType = EffectRendererPipe.getEffectType(effect);
    if (effectType === null) return;

    return EffectRendererPipe.enqueueTask(async () => {
      if (effectType === EffectType.Beauty) {
        effect.attributes?.effectRenderer.forEach((renderer) => {
          EffectRendererPipe.setBeautyEffectStrength(renderer, 0.0);
        });
      } else {
        EffectRendererPipe.effectRenderer?.removeEffect(effectType, effect.effectId);
      }
    });
  }

  public static async setBeautyEffectStrength(effectId: string, strength: number) {
    const beautyEffectType = EffectRendererPipe.getBeautyEffectType(effectId);
    if (beautyEffectType === undefined) return false;

    const nextStrength = Math.min(1, Math.max(0, strength));

    if (EffectRendererPipe.effectRenderer) {
      EffectRendererPipe.effectRenderer.setEffectStrength(beautyEffectType, nextStrength);
      return true;
    }

    return EffectRendererPipe.enqueueTask(async () => {
      const isInitSuccess = await EffectRendererPipe.initialize();
      if (!isInitSuccess || !EffectRendererPipe.effectRenderer) return false;
      EffectRendererPipe.effectRenderer.setEffectStrength(beautyEffectType, nextStrength);
      return true;
    });
  }

  public static async setEffect(
    effect: DecoEffect,
    resourceData: ArrayBuffer | null,
    onTrigger?: (event: EffectTriggerEvent) => void
  ) {
    return EffectRendererPipe.enqueueTask(async () => {
      const effectType = EffectRendererPipe.getEffectType(effect);

      /**
       * 이펙트 라이브러리를 키지 않은 상태에서 이펙트 제거 요청이 들어오면 아무것도 하지 않아도 됨
       */
      if (effectType === null && EffectRendererPipe.initPromise === null) {
        return true;
      }
      const isInitSuccess = await EffectRendererPipe.initialize();
      if (!isInitSuccess || !EffectRendererPipe.effectRenderer) return false;

      if (effectType === null) {
        EffectRendererPipe.removeAllEffects();
        return true;
      }

      try {
        if (effectType !== EffectType.Beauty) {
          const result = await EffectRendererPipe.effectRenderer.setEffect(
            effectType as any,
            resourceData,
            effect.effectId,
            onTrigger
          );

          if (!result) return false;
        }
        try {
          switch (effectType) {
            case EffectType.Beauty: {
              if (resourceData) {
                await EffectRendererPipe.effectRenderer.setBeautyAssets(resourceData);
              }
              const strengths = store.get(beautyEffectStrengthsAtom);
              effect.attributes?.effectRenderer.forEach((renderer) => {
                const strength = getBeautyEffectStrength(strengths, renderer);
                if (strength !== undefined) {
                  EffectRendererPipe.setBeautyEffectStrength(renderer, strength);
                }
              });
              break;
            }
            case EffectType.BackgroundEffect:
              EffectRendererPipe.effectRenderer.setEffectStrength(effectType, 1.0);
              break;
          }
        } catch (e) {
          console.error(e);
        }

        return true;
      } catch (error) {
        Sentry.captureException(error);
        return false;
      }
    });
  }

  private resizeCanvas(canvasEl: HTMLCanvasElement, width: number, height: number) {
    if (canvasEl.width !== width) {
      canvasEl.width = width;
    }
    if (canvasEl.height !== height) {
      canvasEl.height = height;
    }
  }

  private drawImageToCanvas(
    source: CanvasImageSource,
    targetCanvasEl: HTMLCanvasElement,
    width: number,
    height: number
  ) {
    this.resizeCanvas(targetCanvasEl, width, height);
    const context = targetCanvasEl.getContext('2d');
    if (!context) {
      return;
    }

    context.clearRect(0, 0, width, height);
    context.drawImage(source, 0, 0, width, height);
  }

  private copyCanvas(sourceCanvasEl: HTMLCanvasElement, targetCanvasEl: HTMLCanvasElement) {
    this.drawImageToCanvas(
      sourceCanvasEl,
      targetCanvasEl,
      sourceCanvasEl.width,
      sourceCanvasEl.height
    );
  }

  private drawOriginalVideoToCanvas(
    inputVideoElement: HTMLVideoElement,
    canvasEl: HTMLCanvasElement
  ) {
    const { videoWidth, videoHeight } = inputVideoElement;
    this.drawImageToCanvas(inputVideoElement, canvasEl, videoWidth, videoHeight);
  }

  public async renderVideo() {
    const inputVideoElement = this.inputEl;

    if (!(inputVideoElement instanceof HTMLVideoElement)) {
      return;
    }

    this.render = async () => {
      if (
        !inputVideoElement ||
        inputVideoElement.readyState !== HTMLMediaElement.HAVE_ENOUGH_DATA
      ) {
        return;
      }

      const [renderCanvasEl, ...copyTargetCanvasEls] = this.outputCanvasEls;
      if (!renderCanvasEl) {
        return;
      }

      if (EffectRendererPipe.initPromise?.ended && EffectRendererPipe.effectRenderer) {
        const { videoWidth, videoHeight } = inputVideoElement;
        this.resizeCanvas(renderCanvasEl, videoWidth, videoHeight);

        try {
          measure('pipe.effect.render', () =>
            EffectRendererPipe.effectRenderer?.render(inputVideoElement, renderCanvasEl)
          );
        } catch (err) {
          Sentry.captureException(err);
          store.set(handleDecoUnsupportAtom);
          this.drawOriginalVideoToCanvas(inputVideoElement, renderCanvasEl);
        }
      } else {
        this.drawOriginalVideoToCanvas(inputVideoElement, renderCanvasEl);
      }

      copyTargetCanvasEls.forEach((outputCanvasElement) => {
        this.copyCanvas(renderCanvasEl, outputCanvasElement);
      });
    };
  }

  public dispose(): void {
    EffectRendererPipe.removeAllEffects();
    super.dispose();
  }
}

export default EffectRendererPipe;
